import maplibregl, { type Map as MlMap, type Marker } from 'maplibre-gl';
import { BANDS, NO_DATA_COLOR, colorFor } from './scale';
import { cardinal, isCurrent, nationalStaleMinutes, type StationState } from './types';
import { TIDE_STATIONS, isColocatedWithWind } from './tideStations';
import { CURRENT_STATIONS } from './currentStations';
import { EBB_COLOR, FLOOD_COLOR, type StreamAt } from './currentChart';
import { matchesFilters } from './filters';
import { CAMERAS } from './cameras';
import { cameraState } from './cameraStatus';
import { pickVisibleLabels, type LabelBox } from './lib/declutter';
import { metricLabel, nationalVisible, type MetricKey, type MetricSpec } from './lib/mapMetric';
import { withinBounds, type NationalStation } from './sources/nationalBuoys';
import type { CoopsPoint } from './sources/nationalCoops';
import { unionBounds, unwrapGeometry, unwrapLongitudes, type Box } from './lib/antimeridian';
import { obscuredBy, type Obscured } from './lib/panelOverlap';

/**
 * CARTO dark-matter — open, keyless, CORS-enabled. Attribution for CARTO and
 * OpenStreetMap is required and is rendered by MapLibre's attribution control.
 */
const STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';

/**
 * The Bay plus the SF offshore buoy — the water this site is actually about.
 * Bodega and Monterey sit outside this frame on purpose: including them shrank
 * the Bay to a smudge. They stay on the map, and the sidebar flies to them.
 */
const INITIAL_BOUNDS: [[number, number], [number, number]] = [
  [-122.95, 37.42],
  [-121.85, 38.15],
];

/** Breathing room between two kept labels, in screen pixels. */
const LABEL_GAP = 3;

/**
 * Stations are DOM markers rather than an SDF symbol layer. At 18 stations the
 * cost is irrelevant, and it buys crisp SVG, real focusable buttons for keyboard
 * users, and per-marker rotation without hand-rolling a distance field.
 */
const markers = new Map<string, { marker: Marker; el: HTMLElement }>();

export function createMap(container: HTMLElement): MlMap {
  const map = new maplibregl.Map({
    container,
    style: STYLE,
    bounds: INITIAL_BOUNDS,
    fitBoundsOptions: { padding: 40 },
    attributionControl: { compact: true },
  });

  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
  map.addControl(new maplibregl.ScaleControl({ unit: 'nautical' }), 'bottom-left');

  // Bearing stays locked to north. Arrows are rotated in CSS rather than by the
  // Marker, so a rotated map would desync them — and on a wind chart "north is up"
  // is worth more than free rotation anyway.
  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();

  // Labels are thinned individually as the view changes, so a number survives
  // anywhere it still fits. Where one is dropped the arrow colour still carries
  // strength, and the sidebar always has the figure.
  map.on('move', declutterLabels);
  map.on('zoom', declutterLabels);

  return map;
}

/**
 * Hides only the labels that actually collide, best-first.
 *
 * Reads every box before writing any class: interleaving the two would force a
 * layout flush per marker on every frame of a pinch.
 *
 * Boxes come from the rendered rects rather than from projected coordinates
 * because the label sits at a CSS offset from its marker anchor, so the anchor
 * point alone does not say where the text lands.
 */
export function declutterLabels(): void {
  const entries: { label: HTMLElement; box: LabelBox }[] = [];

  // Curated and national labels are thinned in one pass. Two passes would let a
  // national number land on top of a Bay one, each unaware of the other.
  const all: Array<[string, HTMLElement]> = [
    ...[...markers].map(([id, m]) => [id, m.el] as [string, HTMLElement]),
    ...[...nationalMarkers].map(([id, m]) => [`n:${id}`, m.getElement()] as [string, HTMLElement]),
  ];

  for (const [id, el] of all) {
    // display:none while filtered out, which zeroes the rect — such a marker is
    // not on screen and must not reserve space against one that is.
    if (el.classList.contains('is-filtered-out')) continue;

    const label = el.querySelector<HTMLElement>('.marker-label');
    if (!label || !label.textContent?.trim()) continue;

    const r = label.getBoundingClientRect();
    if (r.width === 0) continue;

    // A station with no reading shows an en dash; it ranks below every real
    // number, so a live figure never loses its spot to a placeholder.
    const value = Number(label.dataset.rank);
    entries.push({
      label,
      box: {
        id,
        rank: label.dataset.rank && Number.isFinite(value) ? value : -1,
        left: r.left, right: r.right, top: r.top, bottom: r.bottom,
      },
    });
  }

  const visible = pickVisibleLabels(entries.map((e) => e.box), LABEL_GAP);
  for (const { label, box } of entries) {
    label.classList.toggle('label-hidden', !visible.has(box.id));
  }
}

function markerElement(state: StationState, onSelect: (id: string) => void): HTMLElement {
  const el = document.createElement('button');
  el.type = 'button';
  el.className = 'station-marker';
  el.dataset.station = state.station.id;
  el.innerHTML = `
    <svg class="marker-arrow" viewBox="-12 -12 24 24" width="30" height="30" aria-hidden="true">
      <path d="M0,-9 L6.5,8 L0,4.5 L-6.5,8 Z" />
    </svg>
    <span class="marker-label"></span>`;

  el.addEventListener('click', (e) => {
    e.stopPropagation();
    onSelect(state.station.id);
  });

  return el;
}

/** Whole minutes since a reading, for the age announced to assistive tech. */
function minutesOld(t: Date, now: Date = new Date()): number {
  return Math.max(0, Math.round((now.getTime() - t.getTime()) / 60_000));
}

const ARROW_PATH = 'M0,-9 L6.5,8 L0,4.5 L-6.5,8 Z';
/** Without a bearing there is nothing for an arrow to say, so it becomes a dot. */
const DOT_PATH = 'M0,-5.5 A5.5,5.5 0 1,1 0,5.5 A5.5,5.5 0 1,1 0,-5.5';

function paintMarker(
  el: HTMLElement,
  state: StationState,
  metrics: MetricSpec[],
  metricValues: ReadonlyMap<MetricKey, number>,
): void {
  const { station, latest } = state;
  const usable = isCurrent(state);
  /*
   * Three states, not two.
   *
   * A reading past its staleness window used to render as an en dash — the
   * number existed and was hidden, which throws away real information. An hour
   * old is not nothing; it is a fact about an hour ago, and the reader can
   * decide what that is worth if we say so plainly.
   *
   * Amber rather than red: `.stale` in the popup already uses --warning for
   * exactly this, and --critical is spoken for elsewhere as *broken* — a severe
   * alert, an offline camera, a failed request. A buoy on an hourly rollup is
   * not broken, it is behind.
   */
  const ageing = !usable && latest !== null;
  const hasDir = latest !== null && latest.dirDeg !== null;

  const path = el.querySelector<SVGPathElement>('.marker-arrow path')!;
  const label = el.querySelector<HTMLElement>('.marker-label')!;

  // A stale arrow keeps its speed colour but loses weight: the direction was
  // true when it was reported, and dimming says "then" without repainting it as
  // a different wind.
  path.setAttribute('fill', latest !== null ? colorFor(latest.speedKt) : NO_DATA_COLOR);
  path.setAttribute('d', hasDir ? ARROW_PATH : DOT_PATH);
  el.classList.toggle('is-buoy', station.kind === 'buoy');
  el.classList.toggle('no-data', latest === null);
  el.classList.toggle('is-ageing', ageing);

  // Rotate the glyph, not the marker: rotating the marker element spins the speed
  // label with it and the numbers end up sideways.
  // dirDeg is where the wind comes FROM, so the glyph points downwind.
  const rotation = hasDir ? (latest!.dirDeg! + 180) % 360 : 0;
  el.style.setProperty('--dir', `${rotation}deg`);

  // With metrics selected the label carries those measures instead of wind, one
  // line each. The arrow is untouched: colour and heading still mean wind, and
  // the unit on each number is what keeps them from being read as one figure.
  // Wind is read straight off the state rather than fetched into the value map:
  // every station already carries it, so plumbing it through a loader would be
  // a round trip for a number that is right here.
  const valueOf = (m: MetricSpec): number | undefined =>
    m.key === 'wind'
      ? (usable ? latest!.speedKt : undefined)
      : metricValues.get(m.key);

  label.textContent = '';
  if (metrics.length > 0) {
    for (const m of metrics) {
      const line = document.createElement('span');
      line.className = 'metric-line';
      line.textContent = metricLabel(m, valueOf(m));
      label.append(line);
    }
    label.classList.add('is-metric');
    // Decluttering ranks by the number shown, which no longer parses out of the
    // text once it carries a unit. The first metric decides, so the order the
    // contest is settled in does not shift as values change.
    const lead = valueOf(metrics[0]);
    label.dataset.rank = lead !== undefined ? String(lead) : '';
  } else {
    label.textContent = latest !== null ? String(Math.round(latest.speedKt)) : '–';
    label.classList.remove('is-metric');
    label.dataset.rank = latest !== null ? String(latest.speedKt) : '';
  }

  const dir = usable && latest!.dirDeg !== null
    ? `from ${cardinal(latest!.dirDeg)} ${Math.round(latest!.dirDeg)} degrees`
    : 'direction unavailable';
  // The metrics are announced too, since they are what the visible label reads.
  const shown = metrics
    .map((m) => `, ${m.label.toLowerCase()} ${metricLabel(m, valueOf(m))}`)
    .join('');
  el.setAttribute(
    'aria-label',
    latest !== null
      ? `${station.name}: ${latest.speedKt.toFixed(0)} knots, ${dir}` +
        `${ageing ? `, reported ${minutesOld(latest.t)} minutes ago` : ''}${shown}`
      : `${station.name}: no current reading${shown}`,
  );
  el.title = station.name;
}

export function syncMarkers(
  map: MlMap,
  states: Map<string, StationState>,
  onSelect: (id: string) => void,
  selectedId: string | null = null,
  activeFilters: ReadonlySet<string> = new Set(),
  metrics: MetricSpec[] = [],
  metricValues: ReadonlyMap<MetricKey, ReadonlyMap<string, number>> = new Map(),
): void {
  for (const state of states.values()) {
    const { station } = state;

    let entry = markers.get(station.id);
    if (!entry) {
      const el = markerElement(state, onSelect);
      const marker = new maplibregl.Marker({ element: el })
        .setLngLat([station.lon, station.lat])
        .addTo(map);
      entry = { marker, el };
      markers.set(station.id, entry);
    }

    const forStation = new Map<MetricKey, number>();
    for (const m of metrics) {
      const v = metricValues.get(m.key)?.get(station.id);
      if (v !== undefined) forStation.set(m.key, v);
    }
    paintMarker(entry.el, state, metrics, forStation);
    entry.el.classList.toggle('is-selected', station.id === selectedId);
    // The map follows the list: showing 18 arrows beside a list of 5 would make
    // the filter look broken.
    entry.el.classList.toggle('is-filtered-out', !matchesFilters(station, activeFilters));
  }

  // New markers, changed readings and changed filters all move the boxes around.
  declutterLabels();

  // Colour still means wind, so the legend has to say when the numbers do not.
  const note = document.querySelector<HTMLElement>('.legend-metric');
  if (note) {
    note.hidden = metrics.length === 0;
    if (metrics.length > 0) {
      note.textContent =
        `Labels show ${metrics.map((m) => `${m.label.toLowerCase()} (${m.unit})`).join(', ')}.`;
    }
  }
}

const tideMarkers = new Map<string, Marker>();

/**
 * Tide stations as plain rings, deliberately unlike the wind arrows: no fill
 * from the speed ramp, no rotation. Colour on this map means wind strength, and
 * a tide station has none — so it gets shape instead, and stays off by default
 * so the wind reading is never crowded.
 */
export function syncTideMarkers(
  map: MlMap,
  visible: boolean,
  onSelect: (id: string) => void,
  selectedId: string | null = null,
  national: readonly CoopsPoint[] = [],
): void {
  if (!visible) {
    for (const m of tideMarkers.values()) m.remove();
    tideMarkers.clear();
    return;
  }

  // The Bay's stations are always drawn; the national ones are whatever the
  // caller decided fits this view. Both use the same glyph because both are the
  // same thing — a tide marker shows no value until it is opened either way.
  const wanted = [...TIDE_STATIONS, ...national];
  const keep = new Set(wanted.map((s) => s.id));
  for (const [id, m] of [...tideMarkers]) {
    if (!keep.has(id)) { m.remove(); tideMarkers.delete(id); }
  }

  for (const station of wanted) {
    let marker = tideMarkers.get(station.id);
    if (!marker) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'tide-marker';
      el.title = `${station.name} — tide`;
      el.setAttribute('aria-label', `${station.name}, tide station`);
      el.innerHTML = '<span class="tide-dot"></span>';
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onSelect(station.id);
      });

      marker = new maplibregl.Marker({
        element: el,
        // Six of these share a pier with a wind station; nudge them clear so
        // neither marker buries the other.
        offset: isColocatedWithWind(station) ? [0, 17] : [0, 0],
      })
        .setLngLat([station.lon, station.lat])
        .addTo(map);
      tideMarkers.set(station.id, marker);
    }

    marker.getElement().classList.toggle('is-selected', station.id === selectedId);
  }
}

const currentMarkers = new Map<string, Marker>();

/**
 * Tidal current stations as a chevron pair — a third glyph, distinct from the
 * wind arrows and the tide rings.
 *
 * Deliberately not rotated to the stream: that would need all fourteen stations
 * fetched up front, which CO-OPS answers with 403s, and a static bearing would
 * go stale within the hour anyway. The direction lives in the panel, where it
 * follows the cursor.
 */
export interface CurrentMarkerHandlers {
  onSelect: (id: string) => void;
  /** Pointer entered or left a marker; the caller decides whether to fetch. */
  onHover: (id: string, entering: boolean) => void;
}

export function syncCurrentMarkers(
  map: MlMap,
  visible: boolean,
  handlers: CurrentMarkerHandlers,
  selectedId: string | null = null,
  national: readonly CoopsPoint[] = [],
): void {
  if (!visible) {
    for (const m of currentMarkers.values()) m.remove();
    currentMarkers.clear();
    return;
  }

  const curatedIds = new Set(CURRENT_STATIONS.map((s) => s.id));
  const wanted = [...CURRENT_STATIONS, ...national];
  const keep = new Set(wanted.map((s) => s.id));
  for (const [id, m] of [...currentMarkers]) {
    if (!keep.has(id)) { m.remove(); currentMarkers.delete(id); }
  }

  for (const station of wanted) {
    let marker = currentMarkers.get(station.id);
    if (!marker) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'current-marker';
      el.title = `${station.name} — tidal current`;
      el.setAttribute('aria-label', `${station.name}, tidal current station`);
      // The wave is the resting glyph — static, and deliberately not
      // arrow-shaped, since a still marker cannot know which way the stream
      // sets. The direction badge above it appears only while pointing at it or
      // while scrubbing its chart, so a bearing is only ever shown for a moment
      // it actually belongs to.
      el.innerHTML =
        '<span class="current-dir" aria-hidden="true">' +
        '<svg viewBox="-11 -11 22 22" width="24" height="24">' +
        '<circle class="dir-bg" cx="0" cy="0" r="9.5" />' +
        '<path class="dir-arrow" d="M0,-6 L3.6,4.2 L0,2 L-3.6,4.2 Z" />' +
        '</svg><span class="dir-value"></span></span>' +
        '<svg class="current-wave" viewBox="-9 -7 18 14" width="22" height="18" aria-hidden="true">' +
        '<path d="M-7,-2.5 q3.5,-3 7,0 t7,0" />' +
        '<path d="M-7,3 q3.5,-3 7,0 t7,0" /></svg>';

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        handlers.onSelect(station.id);
      });
      // Hover fetches the stream so the bearing badge can appear, which is
      // affordable for sixteen Bay stations. A national station is fetched on
      // click only: CO-OPS answers one station per request, and a pointer
      // crossing a coastline would fire a burst of them for stations nobody
      // asked about.
      if (curatedIds.has(station.id)) {
        el.addEventListener('pointerenter', () => handlers.onHover(station.id, true));
        el.addEventListener('pointerleave', () => handlers.onHover(station.id, false));
        el.addEventListener('focus', () => handlers.onHover(station.id, true));
        el.addEventListener('blur', () => handlers.onHover(station.id, false));
      }

      marker = new maplibregl.Marker({
        element: el,
        // Several sit on top of a wind or tide station; nudge them clear.
        offset: [0, -17],
      })
        .setLngLat([station.lon, station.lat])
        .addTo(map);
      currentMarkers.set(station.id, marker);
    }

    marker.getElement().classList.toggle('is-selected', station.id === selectedId);
  }
}

/**
 * Paints the direction badges, and only those given.
 *
 * Declarative on purpose: every badge is derived from one map, and any marker
 * absent from it is cleared. Mutating badges imperatively from the hover
 * handler, the chart scrub and the selection separately left them orphaned —
 * hovering a marker, selecting it, then selecting a different station stranded
 * a bearing on the map that nothing owned any more.
 */
export function setCurrentDirections(streams: Map<string, StreamAt>): void {
  for (const [id, marker] of currentMarkers) {
    const el = marker.getElement();
    const stream = streams.get(id);

    if (!stream) {
      el.classList.remove('shows-dir');
      continue;
    }

    const arrow = el.querySelector<SVGPathElement>('.dir-arrow');
    const bg = el.querySelector<SVGCircleElement>('.dir-bg');
    const value = el.querySelector<HTMLElement>('.dir-value');
    if (!arrow || !bg || !value) continue;

    el.classList.add('shows-dir');
    el.classList.toggle('is-slack', stream.phase === 'slack');

    value.textContent = stream.phase === 'slack'
      ? 'slack'
      : stream.speedKt.toFixed(1);

    if (stream.setDeg !== null) {
      // Points the way the water goes, unlike the wind arrows whose bearings
      // are reported as the direction they come FROM.
      arrow.style.transform = `rotate(${stream.setDeg}deg)`;
      // Inline style, not a presentation attribute: any stylesheet rule
      // matching these elements would otherwise win and the phase colour
      // would be lost.
      const color = stream.phase === 'ebb' ? EBB_COLOR : FLOOD_COLOR;
      arrow.style.fill = color;
      bg.style.stroke = color;
      value.style.color = color;
    }
  }
}

const cameraMarkers = new Map<string, Marker>();

/**
 * Camera positions as a small lens glyph — a fourth mark, and again carrying no
 * colour from the wind ramp, since a camera has no reading to encode.
 */
export function syncCameraMarkers(
  map: MlMap,
  visible: boolean,
  onSelect: (id: string) => void,
  selectedId: string | null = null,
): void {
  if (!visible) {
    for (const m of cameraMarkers.values()) m.remove();
    cameraMarkers.clear();
    return;
  }

  for (const camera of CAMERAS) {
    let marker = cameraMarkers.get(camera.id);
    if (!marker) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'camera-marker';
      el.title = `${camera.name} — ${camera.view}`;
      el.setAttribute('aria-label', `Camera: ${camera.name}, ${camera.view}`);
      el.innerHTML =
        '<svg viewBox="-9 -8 18 16" width="21" height="19" aria-hidden="true">' +
        '<rect class="cam-body" x="-8" y="-5.5" width="16" height="11" rx="2.5" />' +
        '<circle class="cam-lens" cx="0" cy="0" r="3.1" />' +
        // Struck through when the camera is serving its outage placeholder.
        '<path class="cam-cross" d="M-7.5,-6.5 L7.5,6.5 M7.5,-6.5 L-7.5,6.5" />' +
        '</svg>';
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onSelect(camera.id);
      });

      marker = new maplibregl.Marker({ element: el, offset: [0, 16] })
        .setLngLat([camera.lon, camera.lat])
        .addTo(map);
      cameraMarkers.set(camera.id, marker);
    }
    const el = marker.getElement();
    el.classList.toggle('is-selected', camera.id === selectedId);

    const state = cameraState(camera.id);
    const down = state === 'offline' || state === 'unreachable';
    el.classList.toggle('is-offline', down);
    el.title = down
      ? `${camera.name} — camera offline`
      : `${camera.name} — ${camera.view}`;
    el.setAttribute(
      'aria-label',
      down
        ? `Camera offline: ${camera.name}`
        : `Camera: ${camera.name}, ${camera.view}`,
    );
  }
}

const ZONE_SOURCE = 'alert-zones';

/**
 * Why a zone is lit, which decides its colour.
 *
 * Amber is the hazard signal used by alert cards and headlines, so a routine
 * forecast zone must not borrow it — an outlined patch of water in warning
 * colours reads as a warning whatever the panel beside it says. Plain zones get
 * the mid blue from the wind ramp instead: visible, and obviously not an alarm.
 */
export type ZoneTone = 'alert' | 'plain';

const ZONE_COLOR: Record<ZoneTone, string> = {
  alert: '#fab219',
  plain: '#6da7ec',
};

export interface ZoneShape {
  geometry: GeoJSON.Geometry;
  tone: ZoneTone;
  /** The zone this shape is, so a click on the map can find its card. */
  id?: string;
}

/**
 * Colour comes from the feature, not the layer.
 *
 * Several zones can be lit at once and they need not agree: a gale offshore and
 * a routine Bay forecast can be on screen together, and painting the pair one
 * colour would either cry wolf over the Bay or mute the gale.
 */
const TONE_PAINT: maplibregl.ExpressionSpecification = [
  'match',
  ['get', 'tone'],
  'alert',
  ZONE_COLOR.alert,
  ZONE_COLOR.plain,
];

/**
 * Draws the water the selected forecasts and alerts apply to.
 *
 * Neither carries geometry — both reference forecast zones — so the shapes are
 * fetched separately and handed in here. Rendered under every station marker: it
 * is context for the readings, not a thing to click.
 *
 * An empty list tears the layers down, so clearing the last selection leaves no
 * stray source behind.
 */
export function showZones(map: MlMap, shapes: ZoneShape[]): void {
  if (shapes.length === 0) {
    clearZones(map);
    return;
  }

  const data: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: shapes.map(({ geometry, tone, id }) => ({
      type: 'Feature',
      // A ring straddling the dateline is drawn the short way round; left as it
      // comes, an Aleutian zone stretches right across the world.
      geometry: unwrapGeometry(geometry as { type: string; coordinates: unknown }) as GeoJSON.Geometry,
      properties: { tone, id: id ?? null },
    })),
  };

  const existing = map.getSource(ZONE_SOURCE) as maplibregl.GeoJSONSource | undefined;
  if (existing) {
    existing.setData(data);
    return;
  }

  map.addSource(ZONE_SOURCE, { type: 'geojson', data });
  map.addLayer({
    id: 'alert-zone-fill',
    type: 'fill',
    source: ZONE_SOURCE,
    paint: { 'fill-color': TONE_PAINT, 'fill-opacity': 0.13 },
  });
  map.addLayer({
    id: 'alert-zone-line',
    type: 'line',
    source: ZONE_SOURCE,
    paint: { 'line-color': TONE_PAINT, 'line-width': 1.5, 'line-opacity': 0.85 },
  });
}

/**
 * Reports which zone was clicked on the map.
 *
 * The panel could already light up the water for a card; this is the way back —
 * a zone on the map finding the card that speaks for it. Registered once on the
 * map rather than per painted layer, since the layers come and go with the
 * selection.
 */
export function onZoneClick(map: MlMap, onSelect: (zoneId: string) => void): void {
  map.on('click', 'alert-zone-fill', (e) => {
    const id = e.features?.[0]?.properties?.id;
    if (typeof id === 'string' && id) onSelect(id);
  });

  // Say it is clickable before it is clicked.
  map.on('mouseenter', 'alert-zone-fill', () => {
    map.getCanvas().style.cursor = 'pointer';
  });
  map.on('mouseleave', 'alert-zone-fill', () => {
    map.getCanvas().style.cursor = '';
  });
}

export function clearZones(map: MlMap): void {
  for (const id of ['alert-zone-fill', 'alert-zone-line']) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(ZONE_SOURCE)) map.removeSource(ZONE_SOURCE);
}

/** How much of the map a panel element is currently sitting on. */
export function mapObscuredBy(map: MlMap, panel: HTMLElement): Obscured {
  return obscuredBy(
    map.getCanvas().getBoundingClientRect(),
    panel.getBoundingClientRect(),
  );
}

/**
 * The part of the map the reader can actually see.
 *
 * `getBounds` describes the whole canvas, but the detail panel sits over its
 * right-hand quarter — 372 pixels of 1360. Asking which regions are "in view"
 * from the full bounds therefore offered regions hidden behind the panel:
 * reading the Bay forecast offered Philadelphia, whose water was on the canvas
 * but under the text.
 *
 * @param hidden How much of the map the panel covers, per edge.
 */
export function visibleViewport(
  map: MlMap,
  hidden: Obscured,
): { north: number; south: number; east: number; west: number } {
  const canvas = map.getCanvas();
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  // Never let the panel swallow the whole map, however small the window.
  const right = Math.max(width * 0.25, width - hidden.right);
  const bottom = Math.max(height * 0.25, height - hidden.bottom);

  const corners = [[0, 0], [right, 0], [right, bottom], [0, bottom]].map(
    ([x, y]) => map.unproject([x, y]),
  );
  // Read the long way round only when that is genuinely the shorter one.
  const lons = unwrapLongitudes(corners.map((c) => c.lng));
  const lats = corners.map((c) => c.lat);

  return {
    west: Math.min(...lons),
    east: Math.max(...lons),
    south: Math.min(...lats),
    north: Math.max(...lats),
  };
}

/**
 * Frames a set of bounding boxes, leaving room for the detail panel.
 *
 * Separate from `fitToShapes` because boxes can be stored wrapped across the
 * dateline, and feeding those through a plain LngLatBounds framed the middle of
 * the Atlantic — Anchorage West's 36 zones read as 369 degrees wide rather than
 * 36.
 */
export function fitToBoxes(map: MlMap, boxes: readonly Box[], hidden: Obscured): void {
  const bounds = unionBounds(boxes);
  if (!bounds) return;

  const [w, s, e, n] = bounds;
  map.fitBounds(
    // The east edge may sit past 180; MapLibre wraps it back itself.
    [[w, s], [e, n]],
    { padding: framePadding(map, hidden), duration: 600 },
  );
}

/**
 * Room to leave around a fit, on whichever side the panel is.
 *
 * Padding larger than the map is impossible to satisfy, and MapLibre answers by
 * doing nothing at all — which is why framing a region silently failed on a
 * phone, where the panel's width is the whole map's.
 */
function framePadding(map: MlMap, hidden: Obscured): maplibregl.PaddingOptions {
  const canvas = map.getCanvas();
  const cap = (px: number, of: number) => Math.min(px, Math.max(0, of / 2 - 40));

  return {
    top: 60,
    left: 60,
    right: 40 + cap(hidden.right, canvas.clientWidth),
    bottom: 60 + cap(hidden.bottom, canvas.clientHeight),
  };
}

/** Frames a set of shapes, leaving room for the detail panel. */
export function fitToShapes(map: MlMap, shapes: GeoJSON.Geometry[], hidden: Obscured): void {
  const bounds = new maplibregl.LngLatBounds();
  let any = false;

  const walk = (coords: unknown): void => {
    if (Array.isArray(coords) && typeof coords[0] === 'number') {
      bounds.extend(coords as [number, number]);
      any = true;
      return;
    }
    if (Array.isArray(coords)) for (const c of coords) walk(c);
  };
  for (const s of shapes) walk((s as { coordinates: unknown }).coordinates);

  if (any) {
    map.fitBounds(bounds, { padding: framePadding(map, hidden), duration: 600 });
  }
}

/**
 * Legend for the speed ramp — a continuous scale always ships one.
 *
 * Collapsible, and closed by default on a phone. At full size it is 175x208,
 * which is 22% of the map on a 390px screen and sits right over the Central Bay
 * where most of the stations are. The scale itself stays visible in the summary;
 * only the marker key and the layer switches fold away.
 */
export function buildLegend(): HTMLElement {
  const root = document.createElement('details');
  root.className = 'legend-scale';
  root.open = !window.matchMedia('(max-width: 760px)').matches;

  const summary = document.createElement('summary');

  const title = document.createElement('span');
  title.className = 'legend-scale-title';
  title.textContent = 'Wind (kt)';
  summary.append(title);

  const ramp = document.createElement('div');
  ramp.className = 'legend-ramp';
  for (const band of BANDS) {
    const cell = document.createElement('span');
    cell.className = 'legend-cell';
    cell.style.background = band.color;
    cell.title = `${band.label} kt`;
    ramp.append(cell);
  }
  summary.append(ramp);

  const labels = document.createElement('div');
  labels.className = 'legend-labels';
  labels.innerHTML = '<span>0</span><span>15</span><span>25+</span>';
  summary.append(labels);
  root.append(summary);

  const body = document.createElement('div');
  body.className = 'legend-body';

  const note = document.createElement('p');
  note.className = 'legend-note';
  note.innerHTML =
    '<i class="legend-buoy"></i>Moored buoy &nbsp; <i class="legend-shore"></i>Shore station<br>' +
    'Arrows point downwind.';
  body.append(note);

  // Says what the numbers mean whenever they stop meaning knots. Hidden the rest
  // of the time, since by default the ramp above already answers it.
  const metricNote = document.createElement('p');
  metricNote.className = 'legend-metric';
  metricNote.hidden = true;
  body.append(metricNote);

  const toggles = document.createElement('div');
  toggles.className = 'legend-toggles';
  toggles.innerHTML =
    '<label class="legend-toggle"><input type="checkbox" id="tide-toggle">' +
    '<i class="legend-tide"></i><span>Tide stations</span></label>' +
    // Nested, like the current layer's own sub-option: subordinate stations
    // outnumber the reference ones two to one, and showing every one of them by
    // default buried the coast.
    '<label class="legend-toggle legend-sub"><input type="checkbox" id="tide-sub-toggle" disabled>' +
    '<span>Include subordinate</span></label>' +
    '<label class="legend-toggle"><input type="checkbox" id="current-toggle">' +
    '<i class="legend-current"></i><span>Current stations</span></label>' +
    // Nested: it only means anything once the current layer is on.
    '<label class="legend-toggle legend-sub"><input type="checkbox" id="current-all-toggle" disabled>' +
    '<span>Show all set &amp; drift</span></label>' +
    '<label class="legend-toggle"><input type="checkbox" id="camera-toggle">' +
    '<i class="legend-camera"></i><span>Cameras</span></label>';
  body.append(toggles);
  root.append(body);

  return root;
}

/* ---------------- National NDBC stations ---------------- */

const nationalMarkers = new Map<string, Marker>();

/**
 * No zoom floor.
 *
 * There was one at zoom 6, and it made stations vanish the moment you pulled
 * back to look at a coastline — the exact gesture that should reveal more of
 * them, not less. Nothing needed it: markers are only created for what is
 * actually on screen, so a zoomed-in view costs a handful either way.
 *
 * The ceiling below is the real protection. Measured at 0.03 ms per marker per
 * view change, so 676 of them add 20 ms to a frame and halve the rate during a
 * pan; 400 keeps a continental view whole while staying inside a frame budget.
 */
const NATIONAL_MAX_MARKERS = 400;

/**
 * Beyond this a national reading wears the ageing treatment.
 *
 * Sized for the hourly rollup these come from: a station reporting on the hour
 * is routinely 60 minutes behind through no fault of its own, so the threshold
 * has to sit above that or the whole layer would be permanently amber and the
 * signal would mean nothing.
 */


/**
 * Draws the national stations that fall inside the current view.
 *
 * Viewport-scoped rather than all-at-once: the data is one request for the whole
 * country, but 650 DOM markers is not something to hand a browser for a map
 * showing one bay. Markers outside the view are removed rather than hidden, so
 * the count stays proportional to what is on screen.
 *
 * These carry no label. The curated stations own the numbers on this map; adding
 * 90 more would swamp a declutterer already working to keep 18 apart.
 */
export function syncNationalMarkers(
  map: MlMap,
  stations: readonly NationalStation[],
  onSelect: (id: string) => void,
  activeFilters: ReadonlySet<string> = new Set(),
  metrics: MetricSpec[] = [],
): string[] {
  const drop = (id: string) => {
    nationalMarkers.get(id)?.remove();
    nationalMarkers.delete(id);
  };

  const b = map.getBounds();
  const visible = withinBounds(stations, {
    north: b.getNorth(), south: b.getSouth(), east: b.getEast(), west: b.getWest(),
  }).filter((st) => nationalVisible(activeFilters, metrics, st));

  if (visible.length > NATIONAL_MAX_MARKERS) {
    // Drop the coastal and fixed stations first. At the zoom where this bites,
    // the coast is a few pixels wide and a harbour station says nothing; the
    // moored buoys out in open water are the reason to be looking at all.
    const buoysFirst = [...visible].sort((a, b) => Number(b.type === 'buoy') - Number(a.type === 'buoy'));
    visible.length = 0;
    visible.push(...buoysFirst.slice(0, NATIONAL_MAX_MARKERS));
  }

  const wanted = new Set(visible.map((s) => s.id));
  for (const id of [...nationalMarkers.keys()]) if (!wanted.has(id)) drop(id);

  for (const s of visible) {
    let marker = nationalMarkers.get(s.id);
    if (!marker) {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = 'station-marker national-marker';
      el.dataset.national = s.id;
      el.innerHTML = `
        <svg class="marker-arrow" viewBox="-12 -12 24 24" width="24" height="24" aria-hidden="true">
          <path d="${ARROW_PATH}" />
        </svg>
        <span class="marker-label"></span>`;
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        onSelect(s.id);
      });
      marker = new maplibregl.Marker({ element: el }).setLngLat([s.lon, s.lat]).addTo(map);
      nationalMarkers.set(s.id, marker);
    }

    const el = marker.getElement();
    const path = el.querySelector<SVGPathElement>('.marker-arrow path')!;
    // A wave buoy has no anemometer, so it gets no arrow and no wind colour —
    // painting it on the speed ramp would put a reading on it that nobody took.
    const hasDir = s.dirDeg !== null && s.speedKt !== null;
    path.setAttribute('fill', s.speedKt === null ? NO_DATA_COLOR : colorFor(s.speedKt));
    path.setAttribute('d', hasDir ? ARROW_PATH : DOT_PATH);
    el.classList.toggle('no-data', s.speedKt === null);
    el.style.setProperty('--dir', `${hasDir ? (s.dirDeg! + 180) % 360 : 0}deg`);

    // Ageing per station, not a blanket flag. These come from an hourly file so
    // most are behind, but a station that reported twenty minutes ago is as
    // current as a Bay one and should not be dimmed for the company it keeps.
    // The same window the panel uses, so a marker that looks current never
    // turns out to be stale on being clicked.
    el.classList.toggle('is-ageing', minutesOld(s.t) > nationalStaleMinutes());

    // Same rule as the curated markers: with a metric selected the label carries
    // that measure and its unit, otherwise it carries wind in knots.
    const label = el.querySelector<HTMLElement>('.marker-label')!;
    const valueOf = (m: MetricSpec): number | undefined => {
      switch (m.key) {
        case 'wind': return s.speedKt ?? undefined;
        case 'waves': return s.waveHeightM === null ? undefined : s.waveHeightM * 3.28084;
        case 'airTemp': return s.airTempC === null ? undefined : s.airTempC * 1.8 + 32;
        case 'waterTemp': return s.waterTempC === null ? undefined : s.waterTempC * 1.8 + 32;
        default: return undefined;   // water level has no national equivalent
      }
    };

    label.textContent = '';
    if (metrics.length > 0) {
      for (const m of metrics) {
        const line = document.createElement('span');
        line.className = 'metric-line';
        line.textContent = metricLabel(m, valueOf(m));
        label.append(line);
      }
      label.classList.add('is-metric');
      const lead = valueOf(metrics[0]);
      label.dataset.rank = lead !== undefined ? String(lead) : '';
    } else {
      label.textContent = s.speedKt === null ? '' : String(Math.round(s.speedKt));
      label.classList.remove('is-metric');
      label.dataset.rank = s.speedKt === null ? '' : String(s.speedKt);
    }
    // The metrics are announced too, since they are what the visible label reads.
    const spoken = metrics
      .map((m) => `, ${m.label.toLowerCase()} ${metricLabel(m, valueOf(m))}`)
      .join('');
    const wind = s.speedKt === null ? 'no wind sensor' : `${Math.round(s.speedKt)} kt`;
    el.title = `${s.name} · ${wind} · ${minutesOld(s.t)} min ago`;
    el.setAttribute(
      'aria-label',
      `${s.name}: ${s.speedKt === null ? 'no wind sensor' : `${s.speedKt.toFixed(0)} knots`}` +
        `${hasDir ? `, from ${cardinal(s.dirDeg!)}` : ''}, reported ${minutesOld(s.t)} minutes ago${spoken}`,
    );
  }

  // Hand back what was actually drawn, so the caller can decide whether this is
  // few enough stations to be worth refreshing from the ten-minute feed.
  return visible.map((s) => s.id);
}
